import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import dayjs from 'dayjs'

import { AppBuilderAPI } from '@makyoapp/makyo-sdk'
import { IVersionHistory } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ProjectStatus } from '@makyoapp/makyo-sdk/models/Projects'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import {
  Avatar,
  AvatarSize,
  Button,
  ButtonColor,
  ButtonVariant,
  DropdownV2,
  Modal,
  ModalGridFooter,
  SearchBox,
  WrappedPagination,
} from '@makyoapp/makyo-ui'

import { queryClient, queryKeys } from '@/lib/react-query'
import { useGetProjectVersionInfo } from '@/resolvers/project/query'
import { useAppContext } from '@/context/AppContext'
import { EVENT_KEYS } from '@/shared/custom-event'
import { useDebounceInput } from '@/shared/makyo-hooks'

type GroupItem = {
  key: string
  name: string
  items: IVersionHistory[]
  latestDate: string | Date
}

// helper: extract a plain text name from rich-text JSON strings used in labels/metadata
const extractPlainName = (raw: unknown): string => {
  try {
    if (typeof raw === 'string') {
      // try parse as JSON array first
      if (raw.trim().startsWith('[') && raw.trim().endsWith(']')) {
        const parsed = JSON.parse(raw)
        return parsed?.[0]?.children?.[0]?.text || ''
      }
      // sometimes the JSON is embedded within a label string like: "Page [ ... ] has been updated"
      if (raw.includes('[') && raw.includes(']')) {
        const jsonString = raw.substring(raw.indexOf('['), raw.lastIndexOf(']') + 1)
        const parsed = JSON.parse(jsonString)
        return parsed?.[0]?.children?.[0]?.text || ''
      }

      return raw
    }
  } catch (_) {
    // ignore
  }
  return ''
}

const cleanVersionLabel = (text: string) => {
  if (typeof text !== 'string') return ''

  // It might be a stringified JSON
  if (text.includes('[') && text.includes(']')) {
    try {
      // This is brittle, but it's the only way to get the JSON
      // from the string.
      const jsonString = text.substring(text.indexOf('['), text.lastIndexOf(']') + 1)
      const parsed = JSON.parse(jsonString)

      const pageName = parsed?.[0]?.children?.[0]?.text
      if (pageName) {
        const prefix = text.substring(0, text.indexOf('['))
        const suffix = text.substring(text.lastIndexOf(']') + 1)
        return `${prefix} ${pageName} ${suffix}`
      }
    } catch (error) {
      /* ignore */
    }
  }
  return text.replace(/^v/, '')
}

export default function VersionHistoryToolbar() {
  const { t } = useTranslation()

  const { project, setProject, version, setVersion } = useAppContext()
  const appID = project?.id || ''

  const [showDropdown, setShowDropdown] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [restoringVersionID, setRestoringVersionID] = useState('')
  const [isRestoring, setIsRestoring] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [debouncedSearch, , , searchValue, setSearchValue] = useDebounceInput('')
  const [params, setParams] = useState({ page: 1, perPage: 100 })

  // get version info from the app builder context
  const { data: versionInfo, isLoading, isFetching } = useGetProjectVersionInfo(appID)

  const versionHistory = useMemo(() => {
    if (!versionInfo) return []
    return versionInfo._VersionHistory.filter((item) => {
      const isCurrentVersion = item._Version._Number === version
      return item._Snapshotted || isCurrentVersion
    })
  }, [versionInfo, version])

  const recentVersions = useMemo(() => {
    return versionHistory.slice(0, 50)
  }, [versionHistory])

  // Build grouped history by `_Code` + entity name (e.g., page name)
  const filteredHistory = useMemo(() => {
    if (!versionInfo || !versionInfo._VersionHistory.length) return [] as any[]

    // filter only the restorable item & current active version
    let filtered = versionHistory

    // apply search filter
    const search = debouncedSearch.replace(/^v/, '').toLowerCase()
    if (search.trim().length > 0) {
      filtered = filtered.filter((item) => {
        const nameFromMeta = extractPlainName(item?._Metadata?.name)
        return (
          item._Version._Number.toLowerCase().includes(search) ||
          item._Version._Label.toLowerCase().includes(search) ||
          item._By._Email.toLowerCase().includes(search) ||
          item._By._Name.toLowerCase().includes(search) ||
          nameFromMeta.toLowerCase().includes(search)
        )
      })
    }

    // apply pagination
    const start = (params.page - 1) * params.perPage
    const end = params.page * params.perPage
    return filtered.slice(start, end)
  }, [debouncedSearch, params.page, params.perPage, versionHistory, versionInfo])

  // const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  // const groupedHistory = useMemo(() => {
  //   const groupsMap: Record<string, GroupItem> = {}
  //   const soloItems: IVersionHistory[] = []
  //   let activeItem: any | null = null

  //   for (const item of filteredHistory) {
  //     const isCurrentVersion = item._Version._Number === version
  //     if (isCurrentVersion) {
  //       activeItem = item
  //       continue
  //     }

  //     const code = item._Code as AppBuilderHistoryCode

  //     if (code === 'page_updated' || code === 'overlay_updated') {
  //       const nameFromMeta =
  //         extractPlainName(item?._Metadata?.name) || extractPlainName(item?._Version?._Label) || 'Untitled'

  //       const key =
  //         code === 'page_updated'
  //           ? `page_updated::${nameFromMeta.toLowerCase()}`
  //           : `overlay_updated::${nameFromMeta.toLowerCase()}`

  //       if (!groupsMap[key]) {
  //         groupsMap[key] = {
  //           key,
  //           name: nameFromMeta,
  //           items: [],
  //           latestDate: item._Date,
  //         }
  //       }
  //       groupsMap[key].items.push(item)
  //       if (dayjs(item._Date).isAfter(groupsMap[key].latestDate)) {
  //         groupsMap[key].latestDate = item._Date
  //       }
  //     } else {
  //       soloItems.push(item)
  //     }
  //   }

  //   const allGroups = Object.values(groupsMap)

  //   // ensure items inside each group are sorted desc by date and compute latestDate
  //   for (const g of allGroups) {
  //     g.items.sort((a, b) => dayjs(b._Date).valueOf() - dayjs(a._Date).valueOf())
  //     g.latestDate = g.items[0]?._Date || g.latestDate
  //   }

  //   // Split to real groups (len > 1) and push singletons back to solo
  //   const realGroups = [] as GroupItem[]
  //   for (const g of allGroups) {
  //     if (g.items.length > 1) realGroups.push(g)
  //     else if (g.items.length === 1) soloItems.push(g.items[0])
  //   }

  //   // Build a single ordered timeline combining groups and items by date
  //   type TimelineEntry =
  //     | { type: 'group'; key: string; latestTs: number; group: GroupItem; startVersion: string; endVersion: string }
  //     | { type: 'item'; key: string; latestTs: number; item: IVersionHistory }

  //   const entries: TimelineEntry[] = []
  //   for (const g of realGroups) {
  //     entries.push({
  //       type: 'group',
  //       key: `group::${g.key}`,
  //       latestTs: dayjs(g.latestDate).valueOf(),
  //       group: g,
  //       startVersion: g.items[0]._Version._Number,
  //       endVersion: g.items[g.items.length - 1]._Version._Number,
  //     })
  //   }
  //   for (const it of soloItems) {
  //     entries.push({ type: 'item', key: `item::${it._id}`, latestTs: dayjs(it._Date).valueOf(), item: it })
  //   }

  //   entries.sort((a, b) => b.latestTs - a.latestTs)

  //   return { activeItem, entries }
  // }, [filteredHistory, version])

  const totalCount = debouncedSearch.trim().length > 0 ? filteredHistory.length : versionHistory.length || 0

  const versionToRestore =
    versionInfo?._VersionHistory.find((item) => item._Version._id === restoringVersionID)?._Version._Number || ''

  // sync version & project status
  useEffect(() => {
    if (!versionInfo) return

    if (version !== versionInfo._Version._Number) {
      setVersion(versionInfo._Version._Number)
    }

    if (project?.status !== versionInfo._Status) {
      setProject((prev) => ({ ...prev!, status: versionInfo._Status }))
    }

    // listen to open version history event
    const handleOpenVersionHistory = () => setShowModal(true)
    window.addEventListener(EVENT_KEYS.OPEN_VERSION_HISTORY, handleOpenVersionHistory)

    return () => {
      window.removeEventListener(EVENT_KEYS.OPEN_VERSION_HISTORY, handleOpenVersionHistory)
    }
  }, [project?.status, setProject, setVersion, version, versionInfo])

  const handleRestoreVersion = async (versionID: string) => {
    if (!appID) return

    setIsRestoring(true)

    AppBuilderAPI.ReqRollbackAppBuilder(appID, versionID)
      .then(() => {
        window.dispatchEvent(new CustomEvent(EVENT_KEYS.APP_BUILDER_RELOAD, { bubbles: true }))
        setShowDropdown(false)
        setShowModal(false)
        setShowConfirmModal(false)
        toast.success(t('Version restored'))
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => {
        setIsRestoring(false)
      })
  }

  const handleSaveVersion = async () => {
    const appID = project?.id || ''
    if (!appID) return

    setIsSaving(true)

    AppBuilderAPI.ReqTakeAppBuilderSnapshot(appID)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.appVersionHistory, appID] })
        toast.success(t('Version saved'))
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const handleShowConfirmation = (versionID: string) => {
    setRestoringVersionID(versionID)
    setShowConfirmModal(true)
  }

  const handleCloseConfirmation = () => {
    setRestoringVersionID('')
    setShowConfirmModal(false)
  }

  const handleConfirmSubmit = async () => {
    if (!restoringVersionID) return

    await handleRestoreVersion(restoringVersionID)
  }

  if (!project) return null

  const versionText = `${project?.status} (v${version})`

  const projectStatusColor = {
    [ProjectStatus.Draft]: 'text-amber-300',
    [ProjectStatus.Published]: 'text-primary-500',
    [ProjectStatus.Unpublished]: 'text-red-500',
  }

  return (
    <>
      {/* Recent version dropdown */}
      <DropdownV2 open={showDropdown} onOpenChange={setShowDropdown}>
        <DropdownV2.Trigger asChild>
          <div className={clsx('flex items-center gap-0.5 cursor-pointer', projectStatusColor[project.status])}>
            <div className='text-m-sm shrink-0'>{versionText}</div>
            <div className='shrink-0 flex items-center'>
              <MaterialIcon icon='keyboard_arrow_down' size={14} />
            </div>
          </div>
        </DropdownV2.Trigger>

        <DropdownV2.Content
          showArrow
          sideOffset={4}
          align='start'
          className='dark text-m-m w-[315px] relative'
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (showConfirmModal) {
              e.preventDefault()
            }
          }}
        >
          {isLoading ? (
            <div className='space-y-4'>
              {[...Array(10)].map((_, i) => (
                <div key={i} className='flex gap-2'>
                  <div className='p-1 shrink-0'>
                    <div className='animate-pulse bg-zinc-300 dark:bg-slate-700 size-7 rounded-full' />
                  </div>

                  <div className='flex-1 space-y-2'>
                    <div className='animate-pulse bg-zinc-300 dark:bg-slate-700 w-18 h-2 rounded-sm' />
                    <div className='animate-pulse bg-zinc-300 dark:bg-slate-700 w-[70%] h-2 rounded-sm' />
                    <div className='animate-pulse bg-zinc-300 dark:bg-slate-700 w-18 h-2 rounded-sm' />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <section>
              <div className='text-[12.5px] px-2.5 py-3 flex items-center justify-between'>
                <div>Recent versions</div>
                <div
                  role='button'
                  className='flex items-center gap-1.5 text-brand-500 cursor-pointer'
                  onClick={() => setShowModal(true)}
                >
                  <span>Version History</span>
                  <MaterialIcon icon='arrow_forward_ios' size={11} />
                </div>
              </div>

              <div className='relative border-y bdr py-2 pl-1 max-h-[400px] overflow-y-auto mk-scroll'>
                {isFetching && (
                  <div className='absolute top-0 left-0 right-0'>
                    <div className='w-full h-[2px] overflow-hidden'>
                      <div className='h-full bg-brand-500 animate-progress-indeterminate' />
                    </div>
                  </div>
                )}

                {recentVersions.map((item) => {
                  const isCurrentVersion = item._Version._Number === version
                  return (
                    // @ts-ignore
                    <div key={item._id} className={clsx('flex gap-2 p-2')}>
                      <div className='p-1 h-fit shrink-0'>
                        <Avatar src={item._By._Avatar} size={AvatarSize.XS} />
                      </div>

                      <div className='flex-1'>
                        <div className={clsx('text-[12px] font-medium', isCurrentVersion && 'text-primary-400')}>
                          v{item._Version._Number}
                          {isCurrentVersion && <span className={'text-primary-400'}> - active</span>}
                        </div>
                        <div className='mt-0.5 text-[11.5px] leading-snug font-poppins'>
                          {cleanVersionLabel(item._Version._Label)}
                        </div>
                        <div className='mt-0.5 text-light text-m-sm'>{dayjs(item._Date).format('LLL')}</div>
                      </div>

                      <div className='shrink-0 size-9'>
                        {item._Snapshotted ? (
                          <Button
                            title='Restore'
                            noPadding
                            squared
                            variant={ButtonVariant.Ghost}
                            icon={<MaterialIcon size={16} icon='settings_backup_restore' />}
                            onClick={() => {
                              handleShowConfirmation(item._Version._id)
                            }}
                          />
                        ) : isCurrentVersion ? (
                          <Button
                            title='Save Version'
                            noPadding
                            squared
                            variant={ButtonVariant.Ghost}
                            onClick={handleSaveVersion}
                            disabled={isSaving}
                            icon={
                              isSaving ? <span className='loading spin' /> : <MaterialIcon size={16} icon='archive' />
                            }
                          />
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </DropdownV2.Content>
      </DropdownV2>

      {/* Version history modal */}
      <Modal
        show={showModal && !showConfirmModal}
        onHide={() => setShowModal(false)}
        closeOnClickOutside={!isRestoring && !isSaving}
        className='dark'
      >
        <Modal.Header onCloseClick={() => setShowModal(false)}>
          <div className='flex-c'>
            <MaterialIcon icon='deployed_code_history' size={16} />
            <span>
              <Trans>Version History</Trans>
            </span>
          </div>
        </Modal.Header>

        <Modal.Body className='modal-md relative' noPadding>
          {isFetching && (
            <div className='absolute top-0 left-0 right-0'>
              <div className='w-full h-[2px] overflow-hidden'>
                <div className='h-full bg-brand-500 animate-progress-indeterminate' />
              </div>
            </div>
          )}

          <div className='p-4 border-b bdr'>
            <SearchBox
              id='search-versions'
              value={searchValue}
              onSearch={setSearchValue}
              onClearSearch={() => setSearchValue('')}
              placeholder={t('Search')}
            />
          </div>

          <div className='p-4 min-h-[300px] max-h-[500px] overflow-y-auto mk-scroll'>
            {filteredHistory.length > 0 ? (
              filteredHistory.map((item) => {
                const isCurrentVersion = item._Version._Number === version
                return (
                  <div key={item._id} className={clsx('flex items-center gap-2 py-2 pb-4')}>
                    <div className='flex-1 flex gap-2'>
                      <div className='p-1 h-fit shrink-0'>
                        <Avatar src={item._By._Avatar} size={AvatarSize.M} />
                      </div>

                      <div className='flex-1'>
                        <div className={clsx('font-medium', isCurrentVersion && 'text-primary-400')}>
                          v{item._Version._Number}
                          {isCurrentVersion && <span className={'text-primary-400'}> - active</span>}
                        </div>
                        <div className='mt-0.5 leading-tight font-poppins'>
                          {cleanVersionLabel(item._Version._Label)}
                        </div>
                        <div className='mt-1 text-[12px] text-light'>
                          <span>{item._By._Email}</span>
                          &nbsp;•&nbsp;
                          <span>{dayjs(item._Date).format('LLL')}</span>
                        </div>
                      </div>
                    </div>

                    <div className='shrink-0 min-w-4'>
                      {item._Snapshotted ? (
                        <Button
                          variant={ButtonVariant.Outlined}
                          icon={<MaterialIcon size={16} icon='settings_backup_restore' />}
                          onClick={() => {
                            handleShowConfirmation(item._Version._id)
                          }}
                        >
                          <Trans>Restore</Trans>
                        </Button>
                      ) : isCurrentVersion ? (
                        <Button
                          variant={ButtonVariant.Outlined}
                          onClick={handleSaveVersion}
                          disabled={isSaving}
                          icon={
                            isSaving ? <span className='loading spin' /> : <MaterialIcon size={16} icon='archive' />
                          }
                        >
                          <Trans>Save Version</Trans>
                        </Button>
                      ) : null}
                    </div>
                  </div>
                )
              })
            ) : (
              <div className='text-light'>No versions found</div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className='!py-3 flex justify-center'>
          <WrappedPagination
            gap='none'
            totalCount={totalCount}
            page={params.page}
            perPage={params.perPage}
            onChange={(_, __, n) => {
              if (params.page !== n) {
                setParams((prev) => ({ ...prev, page: n }))
              }
            }}
          />
        </Modal.Footer>
      </Modal>

      {/* Restore confirmation modal */}
      <Modal fix show={showConfirmModal} onHide={handleCloseConfirmation} closeOnClickOutside={false} className='dark'>
        <Modal.Header onCloseClick={handleCloseConfirmation}>
          <div className='flex-c gap-2'>
            <MaterialIcon size={16} icon='settings_backup_restore' className='text-primary-500' />
            <Trans>Restore Version</Trans>
          </div>
        </Modal.Header>

        <Modal.Body className='modal-md min-h-[175px] overflow-y-auto'>
          <div>
            <p>
              <Trans>Are you sure you want to restore version</Trans>{' '}
              <strong className='text-primary-500'>v{versionToRestore}</strong>?
            </p>

            <p className='mt-1 text-light'>
              <Trans>This action will create a new version that&apos;s a copy of the one you are restoring.</Trans>
            </p>

            <div className='mt-4 bg-amber-500/10 text-amber-400 p-3 rounded-sm border border-amber-500/20'>
              <p className='font-medium text-amber-300'>
                <Trans>Warning: Unsaved changes will be lost</Trans>
              </p>
              <p className='mt-1'>
                <Trans>To avoid losing your current work, please make sure to save your changes first.</Trans>
              </p>
            </div>
          </div>
        </Modal.Body>

        <ModalGridFooter
          onHide={handleCloseConfirmation}
          onCancel={handleCloseConfirmation}
          onSubmit={handleConfirmSubmit}
          cancelLabel={t('Cancel')}
          primaryLabel={t('Restore')}
          colorStyle={ButtonColor.Secondary}
          disabled={isRestoring}
          isLoading={isRestoring}
        />
      </Modal>
    </>
  )
}
